import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  Download as DownloadIcon,
  NoFood as WithdrawalIcon,
  CheckCircle as ComplianceIcon,
  History as TreatmentIcon,
} from '@mui/icons-material';
import {
  useCompliance,
  useTreatmentHistory,
  useWithdrawalStatus,
} from '../../hooks/usePreventiveHealth';
import { SelectChangeEvent } from '@mui/material';
import { useBirdBatches } from '../../hooks/useBirds';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index && <Box pt={3}>{children}</Box>}</div>;
}

const useBatches = () => useBirdBatches({ per_page: 100 });

// ── Compliance Report ──────────────────────────────────────────────────────────

const TRACK_OPTIONS = ['vaccination', 'antiparasitic', 'vitamin', 'biosecurity', 'monitoring'];

const TRACK_COLORS: Record<string, string> = {
  vaccination: '#42a5f5',
  antiparasitic: '#ff9800',
  vitamin: '#66bb6a',
  biosecurity: '#ab47bc',
  monitoring: '#ef5350',
};

const ComplianceReport: React.FC = () => {
  const [batchId, setBatchId] = useState('');
  const [track, setTrack] = useState('');
  const { data: batches } = useBatches();
  const params = React.useMemo(
    () => ({
      ...(batchId ? { batch_id: batchId } : {}),
      ...(track ? { track } : {}),
    }),
    [batchId, track]
  );
  const { data, isLoading } = useCompliance(Object.keys(params).length ? params : undefined);

  const rows: any[] = React.useMemo(
    () => (Array.isArray(data) ? data : (data?.data ?? [])),
    [data]
  );

  const rateColor = (r: number) => (r >= 80 ? '#66bb6a' : r >= 60 ? '#ffa726' : '#ef5350');

  return (
    <Box>
      <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Batch (optional)</InputLabel>
          <Select
            value={batchId}
            label="Batch (optional)"
            onChange={(e: SelectChangeEvent<string>) => setBatchId(e.target.value)}
          >
            <MenuItem value="">All Batches</MenuItem>
            {(Array.isArray(batches?.data) ? batches.data : []).map((b: any) => (
              <MenuItem key={b.id} value={b.id}>
                {b.identifier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Track (optional)</InputLabel>
          <Select
            value={track}
            label="Track (optional)"
            onChange={(e: SelectChangeEvent<string>) => setTrack(e.target.value)}
          >
            <MenuItem value="">All Tracks</MenuItem>
            {TRACK_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TRACK_COLORS[t] }}
                  />
                  {t}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isLoading && <CircularProgress size={20} />}
      </Box>

      {!isLoading && rows.length === 0 && (
        <Alert severity="info">No compliance data available.</Alert>
      )}

      {rows.length > 0 && (
        <Box display="flex" flexDirection="column" gap={2}>
          {rows.map((row: any, idx: number) => {
            const rate = parseFloat(row.compliance_rate ?? 0);
            return (
              <Paper key={idx} sx={{ p: 3, border: '1px solid rgba(255,152,0,0.2)' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  {row.batch?.identifier && (
                    <Typography fontWeight={600}>{row.batch.identifier}</Typography>
                  )}
                  {row.track && (
                    <Chip
                      label={row.track}
                      size="small"
                      sx={{
                        bgcolor: TRACK_COLORS[row.track] ?? '#555',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={4} mb={2}>
                  <Typography
                    variant="h3"
                    fontWeight={700}
                    sx={{ color: rateColor(rate), lineHeight: 1 }}
                  >
                    {rate.toFixed(1)}%
                  </Typography>
                  <Box flex={1}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(rate, 100)}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'rgba(255,255,255,0.1)',
                        '& .MuiLinearProgress-bar': { bgcolor: rateColor(rate), borderRadius: 5 },
                      }}
                    />
                  </Box>
                </Box>
                <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2}>
                  {[
                    { label: 'Total', value: row.total, color: '#ff9800' },
                    { label: 'Completed', value: row.completed, color: '#66bb6a' },
                    { label: 'Missed', value: row.missed, color: '#ef5350' },
                    { label: 'Overdue', value: row.overdue, color: '#ffa726' },
                  ].map((s) => (
                    <Box key={s.label} textAlign="center">
                      <Typography variant="h5" fontWeight={700} sx={{ color: s.color }}>
                        {s.value ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {s.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ── Treatment History Report ───────────────────────────────────────────────────

const TreatmentHistoryReport: React.FC = () => {
  const [batchId, setBatchId] = useState('');
  const { data: batches } = useBatches();
  const { data, isLoading } = useTreatmentHistory(
    batchId ? { batch_id: batchId, per_page: 200 } : { per_page: 200 }
  );

  const rows: any[] = React.useMemo(
    () => (Array.isArray(data) ? data : (data?.data ?? [])),
    [data]
  );

  const TRACK_COLORS: Record<string, string> = {
    vaccination: '#42a5f5',
    antiparasitic: '#ff9800',
    vitamin: '#66bb6a',
    biosecurity: '#ab47bc',
    monitoring: '#ef5350',
  };

  const handleExportCsv = () => {
    if (!rows.length) return;
    const headers = [
      'Date',
      'Event',
      'Track',
      'Product Used',
      'Dose',
      'Birds Treated',
      'Completed By',
      'Observations',
    ];
    const csvRows = rows.map((r) => [
      r.completed_at ?? '',
      r.event?.name ?? r.event ?? '',
      r.track ?? '',
      r.product_used ?? '',
      r.actual_dose ?? '',
      r.birds_treated ?? '',
      r.completed_by ?? '',
      r.observations ?? '',
    ]);
    const csv = [headers, ...csvRows]
      .map((r) => r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `treatment-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={3} alignItems="center" justifyContent="space-between">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Batch (optional)</InputLabel>
          <Select
            value={batchId}
            label="Batch (optional)"
            onChange={(e: SelectChangeEvent<string>) => setBatchId(e.target.value)}
          >
            <MenuItem value="">All Batches</MenuItem>
            {(Array.isArray(batches?.data) ? batches.data : []).map((b: any) => (
              <MenuItem key={b.id} value={b.id}>
                {b.identifier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCsv}
          disabled={!rows.length}
        >
          Export CSV
        </Button>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && rows.length === 0 && (
        <Alert severity="info">No treatment history available.</Alert>
      )}

      {!isLoading && rows.length > 0 && (
        <TableContainer component={Paper} sx={{ border: '1px solid rgba(255,152,0,0.2)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: '#ff9800' } }}>
                <TableCell>Date</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Track</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Dose</TableCell>
                <TableCell>Birds</TableCell>
                <TableCell>Completed By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: any, idx: number) => (
                <TableRow key={idx} hover>
                  <TableCell>{row.completed_at}</TableCell>
                  <TableCell>{row.event?.name ?? row.event}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.track ?? '—'}
                      size="small"
                      sx={{
                        bgcolor: TRACK_COLORS[row.track] ?? '#555',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.product_used ?? '—'}</TableCell>
                  <TableCell>{row.actual_dose ?? '—'}</TableCell>
                  <TableCell>{row.birds_treated ?? '—'}</TableCell>
                  <TableCell>{row.completed_by ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── Withdrawal Status Report ───────────────────────────────────────────────────

const WithdrawalStatusReport: React.FC = () => {
  const [batchId, setBatchId] = useState('');
  const { data: batches } = useBatches();
  const { data, isLoading } = useWithdrawalStatus(batchId ? { batch_id: batchId } : undefined);

  const items: any[] = React.useMemo(
    () => (Array.isArray(data) ? data : (data?.data ?? [])),
    [data]
  );

  return (
    <Box>
      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Batch (optional)</InputLabel>
          <Select
            value={batchId}
            label="Batch (optional)"
            onChange={(e: SelectChangeEvent<string>) => setBatchId(e.target.value)}
          >
            <MenuItem value="">All Batches</MenuItem>
            {(Array.isArray(batches?.data) ? batches.data : []).map((b: any) => (
              <MenuItem key={b.id} value={b.id}>
                {b.identifier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isLoading && <CircularProgress size={20} />}
      </Box>

      {!isLoading && items.length === 0 && (
        <Alert severity="success">No active withdrawal periods.</Alert>
      )}

      {items.some((i) => i.days_remaining > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some batches have active withdrawal periods — egg sales may be restricted.
        </Alert>
      )}

      {items.length > 0 && (
        <Box display="flex" flexDirection="column" gap={2}>
          {items.map((item: any, idx: number) => {
            const days = item.days_remaining ?? 0;
            const chipColor: 'success' | 'error' | 'warning' | 'info' =
              days <= 0 ? 'success' : days <= 3 ? 'error' : days <= 7 ? 'warning' : 'info';
            return (
              <Paper key={idx} sx={{ p: 2.5, border: '1px solid rgba(255,152,0,0.2)' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography fontWeight={600}>{item.event_name}</Typography>
                    {item.batch?.identifier && (
                      <Typography variant="body2" color="text.secondary">
                        Batch: {item.batch.identifier}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Product: {item.product}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Until: {item.egg_withdrawal_until}
                    </Typography>
                  </Box>
                  <Chip
                    label={days <= 0 ? 'Cleared' : `${days}d remaining`}
                    color={chipColor}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ── Cost Summary (placeholder) ─────────────────────────────────────────────────

const CostSummaryReport: React.FC = () => (
  <Alert severity="info" sx={{ mt: 1 }}>
    Cost summary report will be available when treatment cost tracking is enabled in a future
    release.
  </Alert>
);

// ── Main Page ──────────────────────────────────────────────────────────────────

const PHReports: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <ReportIcon sx={{ color: '#ff9800', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>
          Preventive Health Reports
        </Typography>
      </Box>

      <Paper sx={{ px: 2 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ borderBottom: '1px solid rgba(255,152,0,0.15)' }}
        >
          <Tab icon={<ComplianceIcon />} iconPosition="start" label="Compliance" />
          <Tab icon={<TreatmentIcon />} iconPosition="start" label="Treatment History" />
          <Tab icon={<WithdrawalIcon />} iconPosition="start" label="Withdrawal Status" />
          <Tab label="Cost Summary" />
        </Tabs>

        <Box pb={3}>
          <TabPanel value={tab} index={0}>
            <ComplianceReport />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <TreatmentHistoryReport />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <WithdrawalStatusReport />
          </TabPanel>
          <TabPanel value={tab} index={3}>
            <CostSummaryReport />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default PHReports;
